import { useMemo, useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ChevronDown, ChevronUp, X } from "lucide-react";

import { type IndicatorCategory } from "@/lib/lytick/chart-config";
import { getCatalogEntry } from "@/lib/lytick/indicatorRegistry";
import { useChartStore } from "@/stores/chartStore";
import { useReplayStore } from "@/stores/replayStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useUIStore } from "@/stores/uiStore";
import { IndicatorSettings } from "@/components/lytick/indicators/IndicatorSettings";

const INDICATOR_CATEGORY_COLORS: Record<IndicatorCategory, string> = {
  trend: "var(--info)",
  momentum: "var(--warning)",
  volatility: "#A855F7",
};

function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (value >= 10000) return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (value >= 100) return value.toFixed(2);
  if (value >= 1) return value.toFixed(3);
  return value.toFixed(4);
}

/**
 * ChartLegend — Floating OHLC + indicator overlay for the chart canvas.
 * Positioned top-left of the chart area with semi-transparent backdrop.
 *
 * Displays:
 * - Symbol name + timeframe
 * - O/H/L/C values color-coded by candle direction
 * - Active indicator colored pill badges with hover × to remove
 */
export function ChartLegend() {
  const { symbol, timeframe, ohlc, indicators, removeIndicator } = useChartStore(
    useShallow((state) => ({
      symbol: state.symbol,
      timeframe: state.timeframe,
      ohlc: state.ohlc,
      indicators: state.indicators,
      removeIndicator: state.removeIndicator,
    })),
  );

  const { collapsed, setCollapsed } = useSettingsStore(
    useShallow((state) => ({
      collapsed: state.legendCollapsedDesktop,
      setCollapsed: state.setLegendCollapsedDesktop,
    })),
  );

  const { isReplayActive, currentCandle } = useReplayStore(
    useShallow((state) => ({
      isReplayActive: state.isActive,
      currentCandle: state.currentCandle,
    })),
  );

  const candle = useMemo(() => {
    if (isReplayActive && currentCandle) {
      return {
        open: currentCandle.open,
        high: currentCandle.high,
        low: currentCandle.low,
        close: currentCandle.close,
      };
    }
    return ohlc;
  }, [isReplayActive, currentCandle, ohlc]);

  const [settingsId, setSettingsId] = useState<string | null>(null);
  const settingsInstance = useMemo(
    () => indicators.find((i) => i.id === settingsId) ?? null,
    [indicators, settingsId],
  );

  const handleRemoveIndicator = useCallback((id: string) => {
    removeIndicator(id);
  }, [removeIndicator]);

  return (
    <div
      className="pointer-events-none absolute z-20 flex flex-col gap-0.5"
      style={{ top: 8, left: 60 }}
      aria-label="Chart legend"
    >
      {/* Single line: Symbol · Timeframe · [Replay] · O H L C · Change · Toggle */}
      <div
        className="flex items-center"
        style={{ gap: 6, fontFamily: "var(--font-mono)", fontSize: "var(--mono-sm)" }}
      >
        <span
          style={{
            color: "var(--text-primary)",
            fontFamily: "var(--font-ui)",
            fontSize: "var(--text-sm)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}
        >
          {symbol}
        </span>
        <span
          style={{
            color: "var(--text-secondary)",
            fontFamily: "var(--font-ui)",
            fontSize: "var(--text-xs)",
            fontWeight: 600,
          }}
        >
          {timeframe}
        </span>
        {isReplayActive && (
          <span
            className="rounded px-1.5 py-0.5"
            style={{
              color: "var(--lyt-warning)",
              fontFamily: "var(--font-ui)",
              fontSize: "var(--text-xs)",
              fontWeight: 700,
              backgroundColor: "color-mix(in srgb, var(--lyt-warning) 12%, transparent)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Replay
          </span>
        )}
        {!collapsed && (
          <>
            <span style={{ color: "var(--lyt-border-subtle)", margin: "0 2px" }}>·</span>
            <OHLCItem label="O" value={candle?.open} />
            <OHLCItem label="H" value={candle?.high} />
            <OHLCItem label="L" value={candle?.low} />
            <OHLCItem label="C" value={candle?.close} />
            <ChangeItem open={candle?.open} close={candle?.close} />
          </>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand chart legend" : "Collapse chart legend"}
          aria-expanded={!collapsed}
          className="pointer-events-auto rounded flex items-center justify-center"
          style={{
            width: 16,
            height: 16,
            color: "var(--text-muted)",
            borderRadius: "var(--radius-sm)",
            transition: "opacity var(--duration-instant)",
          }}
        >
          {collapsed
            ? <ChevronDown className="size-3" />
            : <ChevronUp className="size-3" />
          }
        </button>
      </div>

      {/* Active Indicator Badges */}
      {!collapsed && indicators.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-0.5 pointer-events-auto">
          {indicators.map((inst) => {
            const catalog = getCatalogEntry(inst.name);
            const categoryColor = catalog
              ? INDICATOR_CATEGORY_COLORS[catalog.category]
              : "var(--text-muted)";
            const label = catalog
              ? `${catalog.shortName} (${inst.calcParams.join(", ")})`
              : inst.name;
            return (
              <button
                key={inst.id}
                type="button"
                onClick={() => setSettingsId(inst.id)}
                className="group/badge inline-flex items-center gap-1 rounded-full px-2 py-0.5 transition-colors"
                style={{
                  backgroundColor: `color-mix(in srgb, ${categoryColor} 12%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${categoryColor} 20%, transparent)`,
                  cursor: "pointer",
                }}
                data-indicator-badge="true"
                aria-label={`${label} settings`}
              >
                <span
                  className="flex-shrink-0 rounded-full"
                  style={{
                    width: 5,
                    height: 5,
                    backgroundColor: categoryColor,
                  }}
                />
                <span
                  style={{
                    color: categoryColor,
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  {label}
                </span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => { e.stopPropagation(); handleRemoveIndicator(inst.id); }}
                  className="flex-shrink-0 rounded-full hover:opacity-70 transition-opacity"
                  style={{ color: categoryColor, padding: "1px" }}
                  aria-label={`Remove ${label}`}
                >
                  <X className="size-3.5" />
                </span>
              </button>
            );
          })}
        </div>
      )}

      {settingsInstance && (
        <IndicatorSettings
          instance={settingsInstance}
          open={settingsId !== null}
          onClose={() => setSettingsId(null)}
        />
      )}
    </div>
  );
}

function OHLCItem({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) {
  return (
    <span>
      <span style={{ color: "var(--lyt-text-muted)", marginRight: 2 }}>{label}</span>
      <span style={{ color: "var(--lyt-text-secondary)", fontWeight: 500 }}>
        {formatPrice(value)}
      </span>
    </span>
  );
}

function ChangeItem({
  open,
  close,
}: {
  open: number | null | undefined;
  close: number | null | undefined;
}) {
  if (!open || !close) return null;
  const diff = close - open;
  const pct = (diff / open) * 100;
  const isPositive = diff >= 0;
  const color = isPositive ? "var(--lyt-profit)" : "var(--lyt-loss)";
  const sign = isPositive ? "+" : "";

  return (
    <span style={{ color, fontWeight: 500 }}>
      {sign}{formatPrice(diff)} ({sign}{pct.toFixed(2)}%)
    </span>
  );
}

